/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 09:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';
import Select, { components } from 'react-select';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import {
  reduxForm,
  getFormSyncErrors,
  FormProps,
  change,
  reset,
  getFormValues,
  initialize
} from 'redux-form';
import {
  ButtonGroup,
  FullscreenBar,
  Button,
  Icon,
  Badge,
  Frame,
  Spinner,
  Popover,
  ActionList,
  Tooltip
} from '@shopify/polaris';
import {
  CaretDownMinor,
  DesktopMajor,
  // MobileMajor,
  ExitMajor,
  HorizontalDotsMinor
} from '@shopify/polaris-icons';
import queryString from 'query-string';

import LeftPanel from './settings';
import Preview from './preview';
import RightPanel from './right-panel';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import LayoutModal from 'src/components/views/theme-settings/layout-modal';
import ConfirmChangeLayoutModal from 'src/components/views/theme-settings/confirm-change-layout-modal';
import ConfirmChangeTemplateModal from 'src/components/views/theme-settings/confirm-change-template-modal';
import CloneSettingModal from '../clone-setting-modal';
import Error404 from 'src/assets/images/shopify-integration/404.svg';
import { ReactComponent as IconFullscreen } from 'src/assets/images/theme-setting/fullscreen.svg';

import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { confirmLeavePage, getThemeReviewUrl } from 'src/helpers';
import { routes } from 'src/routes';
import {
  DEVICE,
  PREVIEW_PAGES,
  ELEMENTS,
  MAIN_LAYOUT_VALUE,
  VIEW_AS_OPTIONS_BY_THEME
} from 'src/constants/theme-settings';
import {
  MENU_ID,
  MENU_SETTING_COLLECTION,
  MENU_SETTING_SEARCH,
  MENU_SETTING_COMMON
} from 'src/constants/theme-settings.jsx';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

import { handleOpenFullscreen } from 'actions/app-integration-actions';
import {
  convertToDataThemeSetting,
  convertToShopifyThemeSetting,
  buildMenuFromThemeSetting,
  ensureDataTranslation
} from 'src/helpers/theme-settings-helper';
import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent, trackFSEvent_ChangeThemeElement } from 'src/integrations/fullStory';

type PropsFromState = {
  synchError: Object,
  editThemeSettingLoading: boolean
};

type PropsFromDispatch = {
  changeSelectedPage: Function,
  handleOpenFullscreen: Function,
  updateField: Function,
  resetForm: Function,
  getFilterData: Function,
  changeDevice: Function
};

type ComposedProps = PropsFromState & PropsFromDispatch & FormProps & WithI18nProps;

class ThemeSettingVisualEditor extends Component<ComposedProps> {
  loadingBar = null;
  isActiveOtherLanguage = false; // Default "en" -> different from default -> change to true
  unsubscribeExit = undefined;

  state = {
    addedElements: [],
    removedElements: [],
    isOpenLayoutModal: false,
    isOpenConfirmChangeLayoutModal: false,
    isOpenConfirmChangeTemplateModal: false,
    activePopoverMenu: false,
    activeModalCloneSetting: false,
    isErrorTheme: false
  };

  componentDidMount() {
    const { changeSelectedPage, getFilterData, i18n, location } = this.props;
    const queries = queryString.parse(location.search);
    const { page } = queries;

    this.getThemeSetting();

    // default collection page
    let selectedPage = {
      ...PREVIEW_PAGES[0],
      label: i18n.translate(PREVIEW_PAGES[0].label)
    };
    if (page === 'search') {
      selectedPage = {
        ...PREVIEW_PAGES[1],
        label: i18n.translate(PREVIEW_PAGES[1].label)
      };
    }
    changeSelectedPage(selectedPage);

    this.handleOpenEditor();

    getFilterData();

    Emitter.on(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING, this.handleLoadingPreview);
    Emitter.on(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING, this.handleCompleteLoadingPreview);

    document.documentElement.style.overflow = 'hidden';
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { clonedSourceThemeSetting } = this.props;
    const {
      firstTimeActivate,
      formValues,
      updateMultiFieldsReducer,
      i18n,
      changeSelectedPage,
      themeSetting,
      themeName,
      clonedSourceThemeSetting: nextClonedSourceThemeSetting,
      clonedSourceThemeSettingFilterLayout
    } = nextProps;

    // Auto show layout modal for the first time accessing theme setting and it is "Default" theme lib
    if (
      firstTimeActivate &&
      !this.state.isOpenLayoutModal &&
      !_.isEmpty(formValues)
      // && this.isDefaultThemeLib()
    ) {
      const mainLayout = _.get(formValues, `${MENU_ID.PRODUCT_ITEM}.mainLayout`, '');
      const subLayout = _.get(formValues, `${MENU_ID.PRODUCT_ITEM}.subLayout`, '');
      // const collectionHeader = _.get(formValues, `${MENU_ID.COLLECTION_HEADER}.layout`, '');
      updateMultiFieldsReducer({
        mainLayout,
        subLayout,
        menuLayoutModalId: 1 // Active page template for the first access
        // collectionHeader
      });

      this.setState({
        isOpenLayoutModal: true
      });
    }

    // Tracking event
    const isTrackedOpenVisual = this.state?.trackingEvent?.isTrackedOpenVisual;
    if (!isTrackedOpenVisual && themeName) {
      this.setState({
        trackingEvent: { isTrackedOpenVisual: true }
      });
      mixpanelV3Helper.trackEventData('Access theme visual design', {
        feature: 'Theme Setup',
        selectedTheme: themeName
      });
    }

    // Compare changed themeSetting to refresh translating i18n locale
    if (this.props.themeSetting !== themeSetting && this.isActiveOtherLanguage) {
      // Update menu when active another i18n locale
      const MenuCollection = buildMenuFromThemeSetting(MENU_SETTING_COLLECTION, themeSetting, i18n);
      const MenuSearch = buildMenuFromThemeSetting(MENU_SETTING_SEARCH, themeSetting, i18n);
      const MenuCommon = buildMenuFromThemeSetting(MENU_SETTING_COMMON, themeSetting, i18n);

      updateMultiFieldsReducer({
        collectionSetting: MenuCollection,
        searchSetting: MenuSearch,
        commonSetting: MenuCommon
      });
      this.isActiveOtherLanguage = false; // Reset to false to prevent infinite rendering
    }

    if (this.props.i18n.locale !== i18n.locale) {
      this.isActiveOtherLanguage = true;
      changeSelectedPage({
        ...PREVIEW_PAGES[0],
        label: i18n.translate(PREVIEW_PAGES[0].label)
      });
    }

    if (
      !_.isEqual(clonedSourceThemeSetting, nextClonedSourceThemeSetting) &&
      !_.isEmpty(nextClonedSourceThemeSetting)
    ) {
      this.handleApplyThemeSetting(
        nextClonedSourceThemeSetting,
        clonedSourceThemeSettingFilterLayout
      );
    }
  }

  componentWillUnmount() {
    const { resetForm, updateMultiFieldsReducer } = this.props;
    resetForm();
    updateMultiFieldsReducer({
      previewSetting: {},
      labelTranslation: {},
      activeMenuItem: '',
      activeMenuItemDetail: null,
      themeSetting: null,
      themeSettingDefaultValue: null,
      themeSettingDefaultValueOriginal: null,
      themeLibTemplate: '',
      themeSettingByName: null
    });

    Emitter.off(EVENT_KEY.LOADING_PREVIEW_THEME_SETTING, this.handleLoadingPreview);
    Emitter.off(EVENT_KEY.COMPLETE_PREVIEW_THEME_SETTING, this.handleCompleteLoadingPreview);

    document.documentElement.style.overflow = 'auto';

    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);

    this.unsubscribeExit();
  }

  async getThemeSetting() {
    const { getThemeSettingById, getThemeSettingDefaultValueById } = this.props;
    const { themeId } = this.props.match.params;

    try {
      const result = await getThemeSettingById(themeId);
      if (result.error) {
        this.setState({
          isErrorTheme: true
        });
        return;
      }
      getThemeSettingDefaultValueById(themeId);
    } catch (e) {
      console.log('ERROR_LOAD_THEME', e);
    }
  }

  toggleModal = (name: string, value: boolean) => {
    this.setState({
      [name]: value
    });
  };

  resetValue = () => {
    const { updateMultiFieldsReducer, originThemeLibTemplate } = this.props;
    updateMultiFieldsReducer({
      mainLayout: MAIN_LAYOUT_VALUE.PRODUCT_ITEM_1,
      subLayout: '',
      collectionHeader: '',
      firstTimeActivate: false,
      themeLibTemplate: originThemeLibTemplate
    });
  };

  handleConfirmChangeLayout = () => {
    const {
      formValues,
      mainLayout: mainLayoutReducer,
      subLayout: subLayoutReducer,
      collectionHeader: collectionHeaderReducer,
      themeSettingDefaultValue: matchThemeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      themeSettingDefaultValueOriginal,
      updateMultiFieldsReducer,
      firstTimeActivate,
      // activeMenuItem,
      updateThemeSetting,
      i18n
    } = this.props;

    const subLayoutSetting = !_.isEmpty(matchThemeSettingDefaultValue?.presetSubLayout)
      ? matchThemeSettingDefaultValue?.presetSubLayout
      : themeSettingDefaultThemeValue?.presetSubLayout;

    const collectionHeaderSetting = !_.isEmpty(
      matchThemeSettingDefaultValue?.presetCollectionHeader
    )
      ? matchThemeSettingDefaultValue?.presetCollectionHeader
      : themeSettingDefaultThemeValue?.presetCollectionHeader;

    // Base on current setting
    let result = _.cloneDeep(formValues);
    let isTheSameFirstTimePreset = false;

    if (mainLayoutReducer === MAIN_LAYOUT_VALUE.MATCH_THEME) {
      const [prefixProductItem] = MENU_ID.PRODUCT_ITEM.split('_');
      const [prefixProductImage] = MENU_ID.PRODUCT_IMAGE.split('_');
      const [prefixProductInfo] = MENU_ID.PRODUCT_INFORMATION.split('_');

      Object.keys(result).forEach(key => {
        if (
          key.includes(prefixProductItem) ||
          key.includes(prefixProductImage) ||
          key.includes(prefixProductInfo)
        ) {
          delete result[key];
        }
      });

      const value = {};
      // Pick element from product items of match theme to overwrite
      Object.keys(themeSettingDefaultValueOriginal?.valueByMenu || {}).forEach(key => {
        if (
          key.includes(prefixProductItem) ||
          key.includes(prefixProductImage) ||
          key.includes(prefixProductInfo)
        ) {
          value[key] = themeSettingDefaultValueOriginal?.valueByMenu[key];
        }
      });
      result = _.merge(value, result);

      result[MENU_ID.PRODUCT_ITEM].mainLayout = mainLayoutReducer;
    } else if (subLayoutReducer) {
      // Changed subLayout
      // Remove all setting related to product item
      const [prefixProductItem] = MENU_ID.PRODUCT_ITEM.split('_');
      const [prefixProductImage] = MENU_ID.PRODUCT_IMAGE.split('_');
      const [prefixProductInfo] = MENU_ID.PRODUCT_INFORMATION.split('_');

      Object.keys(result).forEach(key => {
        if (
          key.includes(prefixProductItem) ||
          key.includes(prefixProductImage) ||
          key.includes(prefixProductInfo)
        ) {
          delete result[key];
        }
      });

      result = _.merge(_.cloneDeep(subLayoutSetting[subLayoutReducer]?.value), result);

      result[MENU_ID.PRODUCT_ITEM].mainLayout = mainLayoutReducer;
      result[MENU_ID.PRODUCT_ITEM].subLayout = subLayoutReducer;
    }

    // Changed collection header
    if (collectionHeaderReducer) {
      // Remove all setting related to collection header
      const [prefix] = MENU_ID.COLLECTION_HEADER.split('_');
      Object.keys(result).forEach(key => {
        if (key.includes(prefix)) {
          delete result[key];
        }
      });

      result = _.merge(
        _.cloneDeep(collectionHeaderSetting[`collectionHeader_${collectionHeaderReducer}`]?.value),
        result
      );

      result[MENU_ID.COLLECTION_HEADER].layout = collectionHeaderReducer;
    }

    // Replace the whole form -> tricky to apply the same layout
    updateThemeSetting({});
    setTimeout(() => {
      updateThemeSetting(result);
    }, 0);

    if (firstTimeActivate) {
      const originalSetting = _.cloneDeep(formValues);
      const originalSubLayout = _.get(originalSetting, `${MENU_ID.PRODUCT_ITEM}.subLayout`, '');
      const originalCollectionHeader = _.get(
        originalSetting,
        `${MENU_ID.COLLECTION_HEADER}.layout`,
        ''
      );
      isTheSameFirstTimePreset =
        originalSubLayout === subLayoutReducer &&
        originalCollectionHeader === collectionHeaderReducer;
    }

    // Update menu
    const MenuCollection = buildMenuFromThemeSetting(MENU_SETTING_COLLECTION, result, i18n);
    const MenuSearch = buildMenuFromThemeSetting(MENU_SETTING_SEARCH, result, i18n);
    const MenuCommon = buildMenuFromThemeSetting(MENU_SETTING_COMMON, result, i18n);

    let objectResetMenuItem = {
      activeMenuItem: '',
      activeMenuItemDetail: null
    };
    // Reset activeMenuItem when is active and apply layout library without activeMenuItem in that setting
    // if (!(activeMenuItem in result)) {
    //   objectResetMenuItem = {
    //     activeMenuItem: '',
    //     activeMenuItemDetail: null
    //   };
    // }

    const changedData = {
      collectionSetting: MenuCollection,
      searchSetting: MenuSearch,
      commonSetting: MenuCommon,
      mainLayout: MAIN_LAYOUT_VALUE.PRODUCT_ITEM_1,
      subLayout: '',
      collectionHeader: '',
      isFormReplaceDirty: !isTheSameFirstTimePreset,
      firstTimeActivate: false,
      ...objectResetMenuItem
    };

    updateMultiFieldsReducer(changedData);

    this.toggleModal('isOpenConfirmChangeLayoutModal', false);

    //Tracking event
    mixpanelV3Helper.trackEventData('Select layout library', {
      feature: 'Theme Settings',
      selectedTheme: this.props.themeName,
      layoutCategory: this.props.mainLayout,
      layoutName: this.props.subLayout
    });

    // Track Full Story event
    trackFSEvent('Select layout library', {
      feature: 'Visual Editor',
      selectedTheme: this.props.themeName,
      layoutCategory: this.props.mainLayout,
      layoutName: this.props.subLayout
    });
  };

  handleConfirmChangeTemplate = async () => {
    const {
      // themeSettingDefaultThemeValue,
      updateFieldReducer,
      updateMultiFieldsReducer,
      updateThemeSetting,
      themeSettingByName,
      getThemeSettingByName,
      boostThemeLib,
      themeLibTemplate,
      firstTimeActivate,
      i18n
    } = this.props;

    let themeSettingResult = _.cloneDeep(themeSettingByName);
    if (firstTimeActivate) {
      updateFieldReducer('firstTimeActivate', false);
    }

    if (themeSettingByName?.themeName !== themeLibTemplate) {
      themeSettingResult = await getThemeSettingByName(themeLibTemplate);
      themeSettingResult = _.get(themeSettingResult, 'payload.response.data', {});
    }
    this.toggleModal('isOpenConfirmChangeTemplateModal', false);

    // Base on current setting
    const result = themeSettingResult?.valueByMenu;

    // In case value viewAs of source theme does not exist in template -> pick the value 2,3,4columns of list options
    const viewAsOptions = VIEW_AS_OPTIONS_BY_THEME[boostThemeLib];
    if (
      result?.toolbar_viewAs?.listType &&
      !viewAsOptions.find(option => option.value === result?.toolbar_viewAs?.listType)
    ) {
      result.toolbar_viewAs.listType = viewAsOptions[0].value; // the 0 element is 2,3,4columns
    }

    updateThemeSetting(result);

    // Update menu
    const MenuCollection = buildMenuFromThemeSetting(MENU_SETTING_COLLECTION, result, i18n);
    const MenuSearch = buildMenuFromThemeSetting(MENU_SETTING_SEARCH, result, i18n);
    const MenuCommon = buildMenuFromThemeSetting(MENU_SETTING_COMMON, result, i18n);

    const changedData = {
      collectionSetting: MenuCollection,
      searchSetting: MenuSearch,
      commonSetting: MenuCommon,
      mainLayout: MAIN_LAYOUT_VALUE.PRODUCT_ITEM_1,
      subLayout: '',
      collectionHeader: '',
      isFormReplaceDirty: true,
      firstTimeActivate: false,
      activeMenuItem: '',
      activeMenuItemDetail: null,
      originThemeLibTemplate: themeLibTemplate
    };

    updateMultiFieldsReducer(changedData);

    //Tracking event
    mixpanelV3Helper.trackEventData('Select Page Template', {
      feature: 'Theme Settings',
      selectedTheme: this.props.themeName,
      selectedTemplate: themeLibTemplate
    });

    // Track Full Story event
    trackFSEvent('Select Page Template', {
      feature: 'Visual Editor',
      selectedTheme: this.props.themeName,
      selectedTemplate: themeLibTemplate
    });
  };

  handleLoadingPreview = () => {
    this.loadingBar && this.loadingBar.continuousStart();
  };

  handleCompleteLoadingPreview = () => {
    this.loadingBar && this.loadingBar.complete();
  };

  isDefaultThemeLib = () => {
    const { boostThemeLib } = this.props;
    return boostThemeLib === 'default';
  };

  togglePopover = () => {
    this.setState(({ activePopoverMenu }) => {
      return { activePopoverMenu: !activePopoverMenu };
    });
  };

  handleOpenCloneSetting = () => {
    this.setState({
      activeModalCloneSetting: true
    });
  };

  handleCloseCloneSettingModal = () => {
    this.setState({
      activeModalCloneSetting: false
    });
  };

  handleSelectTemplate = template => {
    const { updateFieldReducer } = this.props;
    this.setState({
      isOpenLayoutModal: false,
      isOpenConfirmChangeTemplateModal: true
    });
    updateFieldReducer('themeLibTemplate', template?.themeName);
  };

  handleApplyThemeSetting = setting => {
    const { updateThemeSetting, updateMultiFieldsReducer, boostThemeLib } = this.props;

    // In case value viewAs of source theme does not exist in destination theme -> pick the value 2,3,4columns of list options
    const viewAsOptions = VIEW_AS_OPTIONS_BY_THEME[boostThemeLib];
    const clonedSetting = { ...setting };
    if (
      setting?.toolbar_viewAs?.listType &&
      !viewAsOptions.find(option => option.value === setting?.toolbar_viewAs?.listType)
    ) {
      clonedSetting.toolbar_viewAs.listType = viewAsOptions[0].value; // the 0 element is 2,3,4columns
    }

    // Replace the whole form -> tricky to apply the same layout
    updateThemeSetting(clonedSetting);
    updateMultiFieldsReducer({
      isFormReplaceDirty: true
    });
  };

  // Handle preview filter action
  handlePreview = async () => {
    const { themeId } = this.props.match.params;
    const previewUrl = getThemeReviewUrl(themeId);
    window.open(previewUrl, '_blank');
  };

  render() {
    const {
      i18n,
      themeName,
      themePublished,
      handleSubmit,
      history,
      touch,
      device,
      selectedPage,
      updateField,
      formValues,
      themeSettingLoading,
      themeSettingDefaultValueLoading,
      editThemeSettingLoading,
      dirty,
      pristine,
      submitting,
      mainLayout,
      subLayout,
      collectionHeader,
      updateFieldReducer,
      themeSetting,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      themeSettingDefaultValueOriginal,
      isFormReplaceDirty,
      firstTimeActivate,
      boostThemeLib,
      editThemeSettingError,
      themeSettingByNameLoading,
      themeLibTemplate,
      menuLayoutModalId
    } = this.props;
    const { themeId } = this.props.match.params;
    const {
      isOpenLayoutModal,
      isOpenConfirmChangeLayoutModal,
      isOpenConfirmChangeTemplateModal,
      activePopoverMenu,
      activeModalCloneSetting,
      isErrorTheme
    } = this.state;

    const originalSetting = !_.isEmpty(themeSettingDefaultValueOriginal?.valueByMenu)
      ? themeSettingDefaultValueOriginal?.valueByMenu
      : themeSettingDefaultThemeValue?.valueByMenu;

    const pages = PREVIEW_PAGES.map(item => ({
      ...item,
      label: i18n.translate(item.label)
    }));

    const devices = [
      {
        id: DEVICE.DESKTOP,
        content: <Icon source={DesktopMajor} color="base" />,
        panelID: DEVICE.DESKTOP
      },
      {
        id: DEVICE.FULLSCREEN,
        content: <IconFullscreen />,
        panelID: DEVICE.FULLSCREEN
      }
      // {
      //   id: DEVICE.MOBILE,
      //   content: <Icon source={MobileMajor} color="base" />,
      //   panelID: DEVICE.MOBILE
      // }
    ];

    if (themeSettingLoading || themeSettingDefaultValueLoading) {
      return (
        <div className="BC-ThemeSetting-Editor-Page theme-setting-visual-editor">
          <Frame>
            <Spinner accessibilityLabel="Spinner example" size="large" />
          </Frame>
        </div>
      );
    }

    if (isErrorTheme) {
      return (
        <div className="BC-Page BC-Shopify-Integration-Manual-Install">
          <div className="error-container">
            <img src={Error404} alt="404" />
            <h1 className="title">
              {i18n.translate('AdminHTML.ThemeSetting.Common.themeNoExist')}
            </h1>
            <Button onClick={this.handleCloseEditor}>
              {i18n.translate('AdminHTML.ThemeSetting.Common.backToTheme')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="BC-ThemeSetting-Editor-Page theme-setting-visual-editor">
        <FullscreenBar>
          <button
            className="BC-BackAction Polaris-FullscreenBar__BackAction"
            onClick={this.handleCloseEditor}>
            <Tooltip
              content={i18n.translate('AdminHTML.ThemeSetting.Common.exit')}
              zIndexOverride="520">
              <Icon source={ExitMajor} color="base" />
            </Tooltip>
          </button>
          <div className="visual-editor-header">
            <div className="theme-name">
              <p>{themeName}</p>
              <Badge>
                {themePublished
                  ? i18n.translate('AdminHTML.ThemeSetting.Common.published')
                  : i18n.translate('AdminHTML.ThemeSetting.Common.unpublished')}
              </Badge>
            </div>
            <div className="theme-preview">
              {/* <div className="theme-preview-label">
                <span>{i18n.translate('AdminHTML.ThemeSetting.Common.preview')}</span>
              </div> */}
              <div className="select-theme-preview">
                <Select
                  value={selectedPage}
                  options={pages}
                  placeholder={i18n.translate(
                    'AdminHTML.ThemeSetup.ThemeSetup.step2_Theme_Placeholder'
                  )}
                  isDisabled={_.isEmpty(pages)}
                  components={{ DropdownIndicator, IndicatorSeparator: () => null }}
                  onChange={this.handleChangeSelectedPage}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused
                        ? 'rgba(0,126,98,1) !important'
                        : 'hsl(0,0%,80%)',
                      boxShadow: state.isFocused ? '0 0 0 1px rgba(0,126,98,1)' : ''
                    }),
                    option: (styles, { isFocused, isSelected }) => {
                      return {
                        ...styles,
                        backgroundColor: isSelected
                          ? 'rgba(0,126,98,1)'
                          : isFocused
                          ? '#F1F8F5'
                          : '#fff',
                        color: isSelected ? '#FFF' : '#000'
                      };
                    }
                  }}
                />

                <ul className="Polaris-Tabs Polaris-Tabs--fillSpace">
                  {devices.map(item => {
                    return (
                      <li
                        className="Polaris-Tabs__TabContainer"
                        key={item.id}
                        onClick={() => this.handleChangeDevice(item.id)}>
                        <button
                          className={`Polaris-Tabs__Tab ${
                            device === item.id ? 'Polaris-Tabs__Tab--selected' : ''
                          }`}>
                          <span
                            className="Polaris-Tabs__Title"
                            style={item.id === DEVICE.FULLSCREEN ? { height: '36px' } : {}}>
                            {item.content}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="visual-editor-header__action">
              <ButtonGroup>
                <Popover
                  active={activePopoverMenu}
                  activator={
                    <div className="btn-dots">
                      <Button onClick={this.togglePopover}>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon source={HorizontalDotsMinor} />
                        </span>
                      </Button>
                    </div>
                  }
                  onClose={this.togglePopover}>
                  <div className="theme-setting-popover-menu">
                    <Popover.Pane>
                      <ActionList
                        items={[
                          {
                            content: i18n.translate('AdminHTML.ThemeSetting.Common.layoutLibrary'),
                            onAction: () => {
                              this.togglePopover();
                              this.toggleModal('isOpenLayoutModal', true);
                            }
                          },
                          {
                            content: i18n.translate('AdminHTML.ThemeSetting.Common.preview'),
                            onAction: () => {
                              this.togglePopover();
                              this.handlePreview();
                            }
                          },
                          {
                            content: i18n.translate(
                              'AdminHTML.ThemeSetting.CloneSetting.cloneSettingAction'
                            ),
                            onAction: () => {
                              this.togglePopover();
                              this.handleOpenCloneSetting();
                            }
                          }
                        ]}
                      />
                    </Popover.Pane>
                  </div>
                </Popover>

                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={
                    !editThemeSettingError &&
                    (!!Object.keys(this.props.synchError).length || pristine || submitting) &&
                    // Disable when use layout library while there are some errors
                    ((isFormReplaceDirty && !!Object.keys(this.props.synchError).length) ||
                      !isFormReplaceDirty)
                  }
                  loading={editThemeSettingLoading}>
                  {i18n.translate('AdminHTML.Common.Action.save')}
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <LoadingBar color="rgba(0,126,98,1)" shadow={true} ref={e => (this.loadingBar = e)} />
        </FullscreenBar>
        <div className="visual-editor-body">
          <LeftPanel
            touch={touch}
            isDifferentFormOrigin={!_.isEqual(formValues, originalSetting)}
            themeName={themeName}
            upsertElementActions={this.upsertElementActions}
          />
          {!_.isEmpty(themeSetting) &&
            (!_.isEmpty(themeSettingDefaultValue) || !_.isEmpty(themeSettingDefaultThemeValue)) && (
              <Preview themeId={themeId} />
            )}
          <RightPanel themeName={themeName} upsertElementActions={this.upsertElementActions} />
        </div>

        <LayoutModal
          show={isOpenLayoutModal}
          formValues={formValues}
          updateField={updateField}
          onConfirm={() => {
            this.toggleModal('isOpenLayoutModal', false);
            if (firstTimeActivate) {
              this.handleConfirmChangeLayout();
            } else {
              this.toggleModal('isOpenConfirmChangeLayoutModal', true);
            }
          }}
          onClose={() => {
            this.setState({
              isOpenLayoutModal: false
            });
            this.resetValue();
          }}
          onSelectTemplate={this.handleSelectTemplate}
          mainLayout={mainLayout}
          subLayout={subLayout}
          collectionHeader={collectionHeader}
          updateFieldReducer={updateFieldReducer}
          firstTimeActivate={firstTimeActivate}
          boostThemeLib={boostThemeLib}
          isOpenConfirmChangeTemplateModal={isOpenConfirmChangeTemplateModal}
          menuLayoutModalId={menuLayoutModalId}
        />

        <ConfirmChangeLayoutModal
          show={isOpenConfirmChangeLayoutModal}
          onConfirm={this.handleConfirmChangeLayout}
          onCancel={() => {
            this.setState({
              isOpenConfirmChangeLayoutModal: false
            });
            this.resetValue();
          }}
        />

        <ConfirmChangeTemplateModal
          show={isOpenConfirmChangeTemplateModal}
          theme={themeLibTemplate}
          themeSettingByNameLoading={themeSettingByNameLoading}
          onConfirm={this.handleConfirmChangeTemplate}
          onCancel={() => {
            this.toggleModal('isOpenConfirmChangeTemplateModal', false);
            this.resetValue();
          }}
        />

        <CloneSettingModal
          active={activeModalCloneSetting}
          destThemeId={themeId}
          destThemeName={themeName}
          loadingSaveSetting={editThemeSettingLoading}
          isPristine={
            (!!Object.keys(this.props.synchError).length || pristine || submitting) &&
            // Disable when use layout library while there are some errors
            ((isFormReplaceDirty && !!Object.keys(this.props.synchError).length) ||
              !isFormReplaceDirty)
          }
          handleCloseModal={this.handleCloseCloneSettingModal}
          handleSaveBeforeClone={handleSubmit(this.handleSave.bind(this))}
        />

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty || isFormReplaceDirty}
          // Navigate function
          navigate={path => {
            if (dirty || isFormReplaceDirty) {
              const { handleOpenFullscreen, updateFieldReducer } = this.props;
              handleOpenFullscreen(false);
              updateFieldReducer('isFormReplaceDirty', false);
            }
            history.push(path);
          }}
          shouldBlockNavigation={dirty || isFormReplaceDirty}
          isAvailableFullscreen={true}
        />
      </div>
    );
  }

  upsertElementActions = (type: string, properties: Record<string, any>) => {
    const { addedElements, removedElements } = this.state;
    const elementName = properties.elementName;
    if (type === 'add') {
      const removeExists = _.findIndex(removedElements, item => item.elementName === elementName);
      if (removeExists !== -1) removedElements.splice(removeExists, 1);
      else addedElements.push(properties);
    } else if (type === 'remove') {
      const addExists = _.findIndex(addedElements, item => item.elementName === elementName);
      if (addExists !== -1) addedElements.splice(addExists, 1);
      else removedElements.push(properties);
    }

    this.setState({ removedElements, addedElements });
  };

  // handle save settings
  handleSave = () => {
    const {
      formValues,
      editThemeSettings,
      updateFieldReducer,
      labelTranslation,
      initialize,
      i18n,
      themeName,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      themeSetting,
      themeLibTemplate
    } = this.props;
    const { themeId } = this.props.match.params;
    const { addedElements, removedElements } = this.state;

    const files = [];
    const [, saleLabelName] = ELEMENTS.PRODUCT_IMAGE_SALE_LABEL.split('_');
    const [, soldOutLabelName] = ELEMENTS.PRODUCT_IMAGE_SOLD_OUT_LABEL.split('_');
    const [, button1Name] = ELEMENTS.PRODUCT_IMAGE_BUTTON_1.split('_');
    const [, button2Name] = ELEMENTS.PRODUCT_IMAGE_BUTTON_2.split('_');

    // Filter falsy key-value of formValues
    const truthyFormValues = {};
    Object.keys(formValues).forEach(key => {
      if (formValues[key]) {
        return (truthyFormValues[key] = formValues[key]);
      }
    });

    const saleLabelKey = Object.keys(truthyFormValues).find(key => key.includes(saleLabelName));
    const soldOutLabelKey = Object.keys(truthyFormValues).find(key =>
      key.includes(soldOutLabelName)
    );
    const button1Key = Object.keys(truthyFormValues).find(
      key => key.includes(button1Name) && !_.isEmpty(truthyFormValues[key])
    );
    const button2Key = Object.keys(truthyFormValues).find(
      key => key.includes(button2Name) && !_.isEmpty(truthyFormValues[key])
    );

    const saleLabelImg = _.get(truthyFormValues, `${saleLabelKey}.imgSrc`, undefined);
    const soldOutImg = _.get(truthyFormValues, `${soldOutLabelKey}.imgSrc`, undefined);
    const button1Img = _.get(truthyFormValues, `${button1Key}.imgSrc`, undefined);
    const button2Img = _.get(truthyFormValues, `${button2Key}.imgSrc`, undefined);

    // Extract imported files
    if (saleLabelImg && _.isObject(saleLabelImg)) {
      files.push(saleLabelImg);
      delete truthyFormValues[saleLabelKey]?.imgSrc64;
    }
    if (soldOutImg && _.isObject(soldOutImg)) {
      files.push(soldOutImg);
      delete truthyFormValues[soldOutLabelKey]?.imgSrc64;
    }
    if (button1Img && _.isObject(button1Img)) {
      files.push(button1Img);
      delete truthyFormValues[button1Key]?.imgSrc64;
    }
    if (button2Img && _.isObject(button2Img)) {
      files.push(button2Img);
      delete truthyFormValues[button2Key]?.imgSrc64;
    }

    const transformedData = convertToDataThemeSetting(truthyFormValues);
    const shopMetaFieldSetting = convertToShopifyThemeSetting(
      transformedData,
      !_.isEmpty(themeSettingDefaultValue) // Pass theme setting default value for case copying pagination button style infinite scroll from load more
        ? themeSettingDefaultValue
        : themeSettingDefaultThemeValue
    );

    // Re-fill to ensure data translation
    const isNeedReinitialize = ensureDataTranslation(truthyFormValues, labelTranslation, i18n);
    if (isNeedReinitialize) {
      initialize(truthyFormValues);
    }

    editThemeSettings(
      themeId,
      {
        data: truthyFormValues,
        shopMetaFieldSetting,
        themeLibTemplate
      },
      files
    );
    // console.log(JSON.stringify(truthyFormValues, null, 2));
    mixpanelV3Helper.trackChangeThemeElement(
      themeSetting,
      truthyFormValues,
      themeName,
      addedElements,
      removedElements
    );
    // Track Full Story event
    trackFSEvent_ChangeThemeElement(
      themeSetting,
      truthyFormValues,
      themeName,
      addedElements,
      removedElements
    );

    mixpanelV3Helper.trackEventData('Edit theme visual design', {
      feature: 'Theme Settings',
      selectedTheme: themeName,
      enableProductImage: truthyFormValues?.quickView_setting?.showProductImage,
      enableBuyItNow: truthyFormValues?.quickView_setting?.buyItNowBtn?.enable,
      enableAjaxCart: truthyFormValues?.cart_setting?.enableCart
    });

    // Track Full Story event
    trackFSEvent('Edit theme visual design', {
      feature: 'Visual Editor',
      selectedTheme: themeName,
      enableProductImage: truthyFormValues?.quickView_setting?.showProductImage,
      enableBuyItNow: truthyFormValues?.quickView_setting?.buyItNowBtn?.enable,
      enableAjaxCart: truthyFormValues?.cart_setting?.enableCart
    });

    for (const addedElement of addedElements) {
      mixpanelV3Helper.trackEventData('Add visual element', {
        feature: addedElement.feature,
        selectedTheme: addedElement.selectedTheme,
        page: addedElement.page,
        elementName: addedElement.elementName
      });
      // Track Full Story event
      trackFSEvent('Add visual element', {
        feature: 'Visual Editor',
        selectedTheme: addedElement.selectedTheme,
        page: addedElement.page,
        elementName: addedElement.elementName
      });
    }

    for (const removedElement of removedElements) {
      mixpanelV3Helper.trackEventData('Remove visual element', {
        feature: removedElement.feature,
        selectedTheme: removedElement.selectedTheme,
        page: removedElement.page,
        elementName: removedElement.elementName
      });
      // Track Full Story event
      trackFSEvent('Remove visual element', {
        feature: 'Visual Editor',
        selectedTheme: removedElement.selectedTheme,
        page: removedElement.page,
        elementName: removedElement.elementName
      });
    }

    this.setState({ addedElements: [], removedElements: [] });
    updateFieldReducer('isFormReplaceDirty', false);
  };

  handleChangeDevice = selected => {
    this.props.changeDevice(selected);
  };

  handleChangeSelectedPage = value => {
    const { changeSelectedPage } = this.props;
    changeSelectedPage(value);
  };

  // Handle action opening the visual editor
  handleOpenEditor = () => {
    const { handleOpenFullscreen } = this.props;
    this.unsubscribeExit = enterSABFullscreen(this.handleCloseEditor);
    handleOpenFullscreen(true);
  };

  // Handle action closing the visual editor
  handleCloseEditor = () => {
    const { handleOpenFullscreen, history, dirty } = this.props;

    if (!dirty) {
      exitSABFullscreen();
      handleOpenFullscreen(false);
    }
    const url = routes.themeSetup.view();
    history.push(url);
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n, dirty, isFormReplaceDirty } = this.props;
    return confirmLeavePage(event, dirty || isFormReplaceDirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  changeSelectedPage: page => dispatch(ThemeSettingAction.handleChangeSelectedPage(page)),

  updateField: (field, value) => dispatch(change(`ThemeSettingVisualEditorForm`, field, value)),
  resetForm: () => dispatch(reset('ThemeSettingVisualEditorForm')),
  initialize: data => dispatch(initialize('ThemeSettingVisualEditorForm', data)),

  updateThemeSetting: setting =>
    dispatch(ThemeSettingAction.updateThemeSetting(setting, ownProps.i18n)),
  getThemeSettingById: id => dispatch(ThemeSettingAction.getThemeSettingById(id, ownProps.i18n)),
  getThemeSettingDefaultValueById: id =>
    dispatch(ThemeSettingAction.getThemeSettingDefaultValueById(id, ownProps.i18n)),
  getThemeSettingByName: name =>
    dispatch(ThemeSettingAction.getThemeSettingByName(name, ownProps.i18n)),
  editThemeSettings: (themeId, data, files) =>
    dispatch(ThemeSettingAction.editThemeSettings(themeId, data, files, ownProps.i18n)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ThemeSettingAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data => dispatch(ThemeSettingAction.handleUpdateMultiFields(data)),
  changeDevice: device => dispatch(ThemeSettingAction.handleChangeDevice(device))
});

const mapStateToProps = (state, props) => {
  const synchError = getFormSyncErrors('ThemeSettingVisualEditorForm')(state);
  const formValues = getFormValues('ThemeSettingVisualEditorForm')(state) || {};
  const themeSetting = _.get(state, 'themeSetting.themeSetting', {});

  return {
    synchError,
    formValues,
    initialValues: themeSetting,
    activeMenuItem: _.get(state, 'themeSetting.activeMenuItem', ''),
    themeName: _.get(state, 'themeSetting.themeName', ''),
    themePublished: _.get(state, 'themeSetting.themePublished', ''),
    // isDirty: isDirty('ThemeSettingVisualEditorForm')(state),
    device: _.get(state, 'themeSetting.device', DEVICE.DESKTOP),
    selectedPage: _.get(state, 'themeSetting.selectedPage', ''),
    collectionSetting: _.get(state, 'themeSetting.collectionSetting', []),
    searchSetting: _.get(state, 'themeSetting.searchSetting', []),
    commonSetting: _.get(state, 'themeSetting.commonSetting', []),
    themeSetting: _.get(state, 'themeSetting.themeSetting', {}),
    themeSettingLoading: _.get(state, 'themeSetting.themeSettingLoading', false),
    themeSettingSuccess: _.get(state, 'themeSetting.themeSettingSuccess', false),
    themeSettingDefaultValueLoading: _.get(
      state,
      'themeSetting.themeSettingDefaultValueLoading',
      false
    ),
    editThemeSettingLoading: _.get(state, 'themeSetting.editThemeSettingLoading', false),
    editThemeSettingError: _.get(state, 'themeSetting.editThemeSettingError', undefined),
    themeSettingDefaultValue: _.get(state, 'themeSetting.themeSettingDefaultValue', {}),
    themeSettingDefaultThemeValue: _.get(state, 'themeSetting.themeSettingDefaultThemeValue', {}),
    themeSettingDefaultValueOriginal: _.get(
      state,
      'themeSetting.themeSettingDefaultValueOriginal',
      {}
    ),
    mainLayout: _.get(state, 'themeSetting.mainLayout', ''),
    subLayout: _.get(state, 'themeSetting.subLayout', ''),
    collectionHeader: _.get(state, 'themeSetting.collectionHeader', ''),
    isFormReplaceDirty: _.get(state, 'themeSetting.isFormReplaceDirty', false),
    firstTimeActivate: _.get(state, 'themeSetting.firstTimeActivate', false),
    labelTranslation: _.get(state, 'themeSetting.labelTranslation', {}),
    boostThemeLib: _.get(state, 'themeSetting.boostThemeLib', 'default'),
    previewSetting: _.get(state, 'themeSetting.previewSetting', {}),

    clonedSourceThemeSetting: _.get(state, 'themeSetting.clonedSourceThemeSetting', {}),
    clonedSourceThemeSettingFilterLayout: _.get(
      state,
      'themeSetting.clonedSourceThemeSettingFilterLayout',
      ''
    ),
    themeSettingByName: _.get(state, 'themeSetting.themeSettingByName', null),
    themeSettingByNameLoading: _.get(state, 'themeSetting.themeSettingByNameLoading', false),
    themeLibTemplate: _.get(state, 'themeSetting.themeLibTemplate', ''),
    originThemeLibTemplate: _.get(state, 'themeSetting.originThemeLibTemplate', ''),
    menuLayoutModalId: _.get(state, 'themeSetting.menuLayoutModalId')
  };
};

const ThemeSettingVisualEditorForm = reduxForm({
  form: 'ThemeSettingVisualEditorForm', // a unique identifier for this form
  enableReinitialize: true
  // keepDirtyOnReinitialize: true
})(ThemeSettingVisualEditor);

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ThemeSettingVisualEditorForm)
);

const DropdownIndicator = props => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Icon source={CaretDownMinor} color="base" />
      </components.DropdownIndicator>
    )
  );
};
